'use client';
import React, { useState, useMemo } from 'react';
import { toast } from 'sonner';
import { Search, Filter, X, ChevronDown, ChevronUp, Upload, FileText, CheckCircle2, Clock, AlertCircle, Eye, Download, Plus, Paperclip,  } from 'lucide-react';
import { MOCK_FOLLOW_UP_REPORTS, FollowUpReport, ObservationItem, ObservationStatus } from './followUpData';
import ObservationDetailPanel from './ObservationDetailPanel';
import OwnerResponseUpload from './OwnerResponseUpload';

const STATUS_CONFIG: Record<ObservationStatus, { label: string; class: string; icon: React.ReactNode }> = {
  Open: {
    label: 'Open',
    class: 'bg-red-50 text-red-700 border border-red-200',
    icon: <AlertCircle size={11} />,
  },
  'In Progress': {
    label: 'In Progress',
    class: 'bg-amber-50 text-amber-700 border border-amber-200',
    icon: <Clock size={11} />,
  },
  Closed: {
    label: 'Closed',
    class: 'bg-green-50 text-green-700 border border-green-200',
    icon: <CheckCircle2 size={11} />,
  },
};

const SEVERITY_CLASS: Record<string, string> = {
  Critical: 'bg-red-100 text-red-700',
  Severe: 'bg-orange-100 text-orange-700',
  Moderate: 'bg-amber-100 text-amber-700',
  Minor: 'bg-yellow-100 text-yellow-700',
  Normal: 'bg-gray-100 text-gray-600',
};

export default function FollowUpReportsClient() {
  const [reports, setReports] = useState<FollowUpReport[]>(MOCK_FOLLOW_UP_REPORTS);
  const [search, setSearch] = useState('');
  const [filterOwner, setFilterOwner] = useState('');
  const [filterManager, setFilterManager] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [selectedReport, setSelectedReport] = useState<FollowUpReport | null>(null);
  const [selectedObs, setSelectedObs] = useState<ObservationItem | null>(null);
  const [showUpload, setShowUpload] = useState(false);
  const [expandedReportId, setExpandedReportId] = useState<string | null>(null);

  const uniqueOwners = Array.from(new Set(reports.map((r) => r.owner)));
  const uniqueManagers = Array.from(new Set(reports.map((r) => r.manager)));

  const filtered = useMemo(() => {
    let data = [...reports];
    if (search) {
      const q = search.toLowerCase();
      data = data.filter(
        (r) => r.vesselName.toLowerCase().includes(q) || r.imoNumber.includes(q) || r.owner.toLowerCase().includes(q)
      );
    }
    if (filterOwner) data = data.filter((r) => r.owner === filterOwner);
    if (filterManager) data = data.filter((r) => r.manager === filterManager);
    if (filterStatus === 'has-open') data = data.filter((r) => r.openCount > 0);
    if (filterStatus === 'all-closed') data = data.filter((r) => r.openCount === 0 && r.inProgressCount === 0);
    return data;
  }, [reports, search, filterOwner, filterManager, filterStatus]);

  const totalStats = useMemo(() => ({
    totalReports: reports.length,
    totalObs: reports.reduce((s, r) => s + r.totalObservations, 0),
    totalOpen: reports.reduce((s, r) => s + r.openCount, 0),
    totalClosed: reports.reduce((s, r) => s + r.closedCount, 0),
  }), [reports]);

  const handleUpdateObservation = (reportId: string, obsId: string, updates: Partial<ObservationItem>) => {
    setReports((prev) =>
      prev.map((r) => {
        if (r.id !== reportId) return r;
        let updatedObs = r.observations.map((o) => (o.id === obsId ? { ...o, ...updates } : o));
        const openCount = updatedObs.filter((o) => o.status === 'Open').length;
        const inProgressCount = updatedObs.filter((o) => o.status === 'In Progress').length;
        const closedCount = updatedObs.filter((o) => o.status === 'Closed').length;
        return { ...r, observations: updatedObs, openCount, inProgressCount, closedCount };
      })
    );
    if (selectedObs?.id === obsId) {
      setSelectedObs((prev) => (prev ? { ...prev, ...updates } : null));
    }
    toast.success('Observation updated');
  };

  const handleOwnerResponseUpload = (reportId: string, updates: Partial<ObservationItem>[]) => {
    setReports((prev) =>
      prev.map((r) => {
        if (r.id !== reportId) return r;
        let updatedObs = [...r.observations];
        updates.forEach((upd) => {
          updatedObs = updatedObs.map((o) => (o.ref === upd.ref ? { ...o, ...upd } : o));
        });
        const openCount = updatedObs.filter((o) => o.status === 'Open').length;
        const inProgressCount = updatedObs.filter((o) => o.status === 'In Progress').length;
        const closedCount = updatedObs.filter((o) => o.status === 'Closed').length;
        return { ...r, observations: updatedObs, openCount, inProgressCount, closedCount, lastOwnerResponseDate: new Date().toISOString().split('T')[0] };
      })
    );
    toast.success('Owner response report processed and platform updated');
    setShowUpload(false);
  };

  const handleGenerateReport = (report: FollowUpReport) => {
    // TODO: POST /api/follow-up-reports/generate — generate PDF/Excel report
    toast.success(`Follow-up report for ${report.vesselName} ready for download`);
  };

  const clearFilters = () => {
    setSearch('');
    setFilterOwner('');
    setFilterManager('');
    setFilterStatus('');
  };

  const activeFilterCount = [filterOwner, filterManager, filterStatus].filter(Boolean).length + (search ? 1 : 0);

  return (
    <div className="flex flex-col min-h-screen">
      {/* Header */}
      <div className="px-6 py-5 border-b bg-white sticky top-0 z-20" style={{ borderColor: 'hsl(var(--border))' }}>
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-xl font-bold">Follow-Up Reports</h1>
            <p className="text-xs text-muted mt-0.5">Observation tracking · Corrective actions · Owner responses</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <button className="btn-secondary text-xs" onClick={() => setShowUpload(true)}>
              <Upload size={14} /> Upload Owner Response
            </button>
            <button className="btn-primary text-xs" onClick={() => toast.info('Select an inspection from Data Board to generate a follow-up report')}>
              <Plus size={14} /> New Report
            </button>
          </div>
        </div>
      </div>

      <div className="flex-1 px-6 py-5 space-y-4 max-w-screen-2xl mx-auto w-full">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            { label: 'Active Reports', value: totalStats.totalReports, color: 'hsl(var(--primary))' },
            { label: 'Total Observations', value: totalStats.totalObs, color: 'hsl(var(--foreground))' },
            { label: 'Open Items', value: totalStats.totalOpen, color: '#dc2626' },
            { label: 'Closed Items', value: totalStats.totalClosed, color: '#16a34a' },
          ].map(({ label, value, color }) => (
            <div key={`stat-${label}`} className="bg-white rounded-xl border p-4 shadow-card" style={{ borderColor: 'hsl(var(--border))' }}>
              <p className="text-xs text-muted mb-1">{label}</p>
              <p className="text-2xl font-bold tabular-nums" style={{ color }}>{value}</p>
            </div>
          ))}
        </div>

        {/* Search + Filters */}
        <div className="bg-white rounded-xl border shadow-card p-4" style={{ borderColor: 'hsl(var(--border))' }}>
          <div className="flex gap-3 flex-wrap items-center">
            <div className="relative flex-1 min-w-48">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted" />
              <input
                className="input-field pl-8 text-sm"
                placeholder="Search vessel, IMO, owner..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <button
              className={`btn-secondary text-xs flex items-center gap-2 ${activeFilterCount > 0 ? 'border-primary text-primary' : ''}`}
              onClick={() => setShowFilters(!showFilters)}
            >
              <Filter size={14} /> Filters
              {activeFilterCount > 0 && (
                <span className="px-1.5 py-0.5 rounded-full text-xs font-bold" style={{ background: 'hsl(var(--primary))', color: '#fff' }}>
                  {activeFilterCount}
                </span>
              )}
            </button>
            {activeFilterCount > 0 && (
              <button className="text-xs text-muted hover:text-destructive flex items-center gap-1" onClick={clearFilters}>
                <X size={12} /> Clear
              </button>
            )}
          </div>
          {showFilters && (
            <div className="mt-4 pt-4 border-t grid grid-cols-2 md:grid-cols-3 gap-3" style={{ borderColor: 'hsl(var(--border))' }}>
              <div>
                <label className="block text-xs font-semibold mb-1 text-muted">Owner</label>
                <select className="input-field text-xs" value={filterOwner} onChange={(e) => setFilterOwner(e.target.value)}>
                  <option value="">All owners</option>
                  {uniqueOwners.map((o) => <option key={`fo-${o}`} value={o}>{o}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold mb-1 text-muted">Ship Manager</label>
                <select className="input-field text-xs" value={filterManager} onChange={(e) => setFilterManager(e.target.value)}>
                  <option value="">All managers</option>
                  {uniqueManagers.map((m) => <option key={`fm-${m}`} value={m}>{m}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold mb-1 text-muted">Report Status</label>
                <select className="input-field text-xs" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
                  <option value="">All reports</option>
                  <option value="has-open">Has open items</option>
                  <option value="all-closed">All items closed</option>
                </select>
              </div>
            </div>
          )}
        </div>

        {/* Reports List */}
        <div className="space-y-3">
          {filtered.map((report) => {
            const isExpanded = expandedReportId === report.id;
            const closePct = report.totalObservations > 0
              ? Math.round((report.closedCount / report.totalObservations) * 100)
              : 0;

            return (
              <div key={`report-${report.id}`} className="bg-white rounded-2xl border shadow-card overflow-hidden" style={{ borderColor: 'hsl(var(--border))' }}>
                {/* Report Header */}
                <div className="px-5 py-4">
                  <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-bold text-sm">{report.vesselName}</h3>
                        <span className="text-xs text-muted font-mono">IMO {report.imoNumber}</span>
                        {report.openCount > 0 && (
                          <span className="px-2 py-0.5 rounded-full text-xs font-bold bg-red-100 text-red-700">
                            {report.openCount} open
                          </span>
                        )}
                        {report.openCount === 0 && report.inProgressCount === 0 && (
                          <span className="px-2 py-0.5 rounded-full text-xs font-bold bg-green-100 text-green-700">
                            All closed
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-3 mt-1 flex-wrap">
                        <span className="text-xs text-muted">{report.owner}</span>
                        <span className="text-xs text-muted">·</span>
                        <span className="text-xs text-muted">{report.manager}</span>
                        <span className="text-xs text-muted">·</span>
                        <span className="text-xs text-muted">Inspected {report.inspectionDate}</span>
                        {report.lastOwnerResponseDate && (
                          <>
                            <span className="text-xs text-muted">·</span>
                            <span className="text-xs text-green-600">Owner responded {report.lastOwnerResponseDate}</span>
                          </>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <button
                        className="btn-secondary text-xs"
                        onClick={() => handleGenerateReport(report)}
                      >
                        <Download size={13} /> Export
                      </button>
                      <button
                        className="btn-secondary text-xs"
                        onClick={() => setExpandedReportId(isExpanded ? null : report.id)}
                      >
                        <Eye size={13} />
                        {isExpanded ? 'Hide' : 'View'} Observations
                        {isExpanded ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
                      </button>
                    </div>
                  </div>

                  {/* Progress Bar */}
                  <div className="mt-3">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs text-muted">Closure progress</span>
                      <span className="text-xs font-semibold">{closePct}% closed</span>
                    </div>
                    <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full transition-all"
                        style={{
                          width: `${closePct}%`,
                          background: closePct === 100 ? '#16a34a' : closePct > 50 ? '#d97706' : '#dc2626',
                        }}
                      />
                    </div>
                    <div className="flex items-center gap-4 mt-1.5">
                      <span className="text-xs text-red-600 font-medium">{report.openCount} Open</span>
                      <span className="text-xs text-amber-600 font-medium">{report.inProgressCount} In Progress</span>
                      <span className="text-xs text-green-600 font-medium">{report.closedCount} Closed</span>
                    </div>
                  </div>
                </div>

                {/* Observations Table */}
                {isExpanded && (
                  <div className="border-t overflow-x-auto" style={{ borderColor: 'hsl(var(--border))' }}>
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="bg-gray-50 border-b" style={{ borderColor: 'hsl(var(--border))' }}>
                          {['Ref', 'Cat', 'Observation', 'Severity', 'LSD', 'Corrective Action', 'Evidence', 'Status', 'Due', ''].map((h) => (
                            <th key={`th-${h}`} className="px-3 py-2.5 text-left font-semibold text-muted whitespace-nowrap">{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {report.observations.map((obs) => {
                          const sc = STATUS_CONFIG[obs.status];
                          return (
                            <tr key={`obs-${obs.id}`} className="border-b hover:bg-gray-50 transition-colors" style={{ borderColor: 'hsl(var(--border))' }}>
                              <td className="px-3 py-2.5 font-mono font-bold text-primary whitespace-nowrap">{obs.ref}</td>
                              <td className="px-3 py-2.5 whitespace-nowrap">
                                <span className="px-1.5 py-0.5 rounded text-xs font-bold bg-blue-50 text-blue-700">{obs.category}</span>
                              </td>
                              <td className="px-3 py-2.5 max-w-xs">
                                <p className="truncate" title={obs.description}>{obs.description}</p>
                              </td>
                              <td className="px-3 py-2.5 whitespace-nowrap">
                                <span className={`px-1.5 py-0.5 rounded text-xs font-semibold ${SEVERITY_CLASS[obs.severity] || 'bg-gray-100 text-gray-600'}`}>
                                  {obs.severity}
                                </span>
                              </td>
                              <td className="px-3 py-2.5 font-mono font-bold whitespace-nowrap">{obs.lsdScore}</td>
                              <td className="px-3 py-2.5 max-w-xs">
                                {obs.correctiveAction ? (
                                  <p className="truncate text-green-700" title={obs.correctiveAction}>{obs.correctiveAction}</p>
                                ) : (
                                  <span className="text-muted italic">Awaiting owner</span>
                                )}
                              </td>
                              <td className="px-3 py-2.5 whitespace-nowrap">
                                {obs.ownerEvidence.length > 0 ? (
                                  <span className="flex items-center gap-1 text-green-600">
                                    <Paperclip size={11} /> {obs.ownerEvidence.length}
                                  </span>
                                ) : (
                                  <span className="text-muted">—</span>
                                )}
                              </td>
                              <td className="px-3 py-2.5 whitespace-nowrap">
                                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${sc.class}`}>
                                  {sc.icon} {sc.label}
                                </span>
                              </td>
                              <td className="px-3 py-2.5 whitespace-nowrap text-muted">{obs.dueDate}</td>
                              <td className="px-3 py-2.5 whitespace-nowrap">
                                <button
                                  className="text-xs text-primary hover:underline font-medium"
                                  onClick={() => { setSelectedReport(report); setSelectedObs(obs); }}
                                >
                                  Edit
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            );
          })}

          {filtered.length === 0 && (
            <div className="bg-white rounded-2xl border p-12 text-center" style={{ borderColor: 'hsl(var(--border))' }}>
              <FileText size={32} className="mx-auto text-muted mb-3" />
              <p className="font-semibold text-sm">No follow-up reports found</p>
              <p className="text-xs text-muted mt-1">Adjust filters or generate a report from an inspection record</p>
            </div>
          )}
        </div>
      </div>

      {/* Observation Edit Panel */}
      {selectedObs && selectedReport && (
        <ObservationDetailPanel
          observation={selectedObs}
          reportId={selectedReport.id}
          onClose={() => { setSelectedObs(null); setSelectedReport(null); }}
          onUpdate={(obsId, updates) => handleUpdateObservation(selectedReport.id, obsId, updates)}
        />
      )}

      {/* Owner Response Upload Modal */}
      {showUpload && (
        <OwnerResponseUpload
          reports={reports}
          onClose={() => setShowUpload(false)}
          onApply={handleOwnerResponseUpload}
        />
      )}
    </div>
  );
}
